/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Inject, Injectable} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {NbMenuItem, NbSidebarState} from '@nebular/theme';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, shareReplay, take, tap} from 'rxjs/operators';
import {ApiResponse} from '../interfaces/ApiResponse';
import {CartProduct} from '../interfaces/CartProduct';
import {CartProductReplace} from '../interfaces/CartProductReplace';
import {Contract} from '../interfaces/Contract';
import {Customer} from '../interfaces/Customer';
import {CustomerGroup} from '../interfaces/CustomerGroup';
import {Environment} from '../interfaces/Environment';
import {InitData} from '../interfaces/InitData';
import {Settings} from '../interfaces/Settings';
import {CompoApiService} from './compo-api.service';

@Injectable({
  providedIn: 'root',
})
export class StoreService {

  private static CART = 'cart';
  private customerGroup$: BehaviorSubject<CustomerGroup>;
  private currentProduct$: BehaviorSubject<string>;
  private replaceAnalog$: BehaviorSubject<CartProductReplace>;
  private customer$: BehaviorSubject<Customer>;
  private currentContract$: BehaviorSubject<Contract>;
  private menu$: Observable<NbMenuItem[]>;

  constructor(readonly apiService: CompoApiService,
              readonly title: Title,
              readonly meta: Meta,
              @Inject('environment') readonly environment: Environment
  ) {
    this.customerGroup$ = new BehaviorSubject<CustomerGroup>({id: 0, header: 'Регионы', warehouseGroup: []});
    this.customer$ = new BehaviorSubject<Customer>(this.getCustomer());
    this.currentProduct$ = new BehaviorSubject<string>(null);
    this.currentContract$ = new BehaviorSubject<Contract>(null);
    this.replaceAnalog$ = new BehaviorSubject<CartProductReplace>(null);
    this.menu$ = this.apiService.get<NbMenuItem[]>('/menu').pipe(
      map(response => response.data));
  }

  initCustomerGroup(): void {
    const customerGroup = this.getCustomerGroupLocal();
    if (customerGroup) {
      this.nextCustomerGroup(customerGroup);
    } else {
      this.getDefaultCustomerGroup().pipe(
        tap(response => this.setCustomerGroup(response)),
        take(1)
      ).subscribe();
    }
  }

  initData(): Observable<ApiResponse<InitData>> {
    return this.apiService
      .get<InitData>('/init')
      .pipe(tap(response => this.handleResponse(response)));
  }

  getDefaultCustomerGroup(): Observable<CustomerGroup> {
    const uri = '/customer-group/default';
    return this.apiService.get<CustomerGroup>(uri).pipe(map(response => response.data));
  }

  handleResponse(response: ApiResponse<InitData>): void {
    const user = response.data.user;
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('settings', JSON.stringify(response.data.settings));
    if (this.getCustomerGroupLocal() === null) {
      this.setCustomerGroup(user.customerGroup);
    }

    if (!this.getSidebarState()) {
      this.setSidebarState(this.environment.isOpenCabinet ? 'compacted' : 'expanded');
    }
    this.customer$.next(user);
  }

  public getSharedCartCustomerGroup(syncUid): Observable<CustomerGroup> {
    console.log('getSharedCartCustomerGroup')
    const uri = '/cart/share/customer-group/' + syncUid;
    return this.apiService.get<CustomerGroup>(uri).pipe(map(response => response.data));
  }

  getReplaceAnalog(): Observable<CartProductReplace> {
    return this.replaceAnalog$.asObservable();
  }

  nextReplaceAnalog(data: CartProductReplace): void {
    this.replaceAnalog$.next(data);
  }

  nextCustomerGroup(group: CustomerGroup) {
    if (group) {
      this.customerGroup$.next(group);
    }
  }

  getCustomerGroup(): Observable<CustomerGroup> {
    return this.customerGroup$.asObservable();
  }

  nextCurrentProduct(syncUid: string) {
    this.currentProduct$.next(syncUid);
  }

  getCurrentProduct(): Observable<string> {
    return this.currentProduct$.asObservable();
  }

  nextCurrentContract(contract: Contract) {
    this.currentContract$.next(contract);
  }

  getCurrentContract(): Observable<Contract> {
    return this.currentContract$.asObservable();
  }

  toggleSidebarState() {
    const state = this.getSidebarState();

    if (state === 'compacted') {
      this.setSidebarState('expanded');
    }

    if (state === 'expanded') {
      this.setSidebarState('compacted');
    }

  }

  getSessionEntity<T>(entity: string): T {
    return this.getSessionObject<T>(entity);
  }

  getCustomer(): Customer {
    return this.getLocalObject<Customer>('user');
  }

  getCustomerAsync(): Observable<Customer> {
    return this.customer$.asObservable();
  }

  nextCustomer(customer: Customer): void {
    localStorage.setItem('user', JSON.stringify(customer));
    this.customer$.next(customer);
  }

  getCart(): CartProduct[] {
    const cart = localStorage.getItem(StoreService.CART);

    return cart === null ? [] : JSON.parse(cart);
  }

  setCart(cart: CartProduct[]) {
    localStorage.setItem(StoreService.CART, JSON.stringify(cart));
  }

  delCart() {
    localStorage.removeItem(StoreService.CART);
  }

  getSettings(): Settings {
    return this.getLocalObject<Settings>('settings');
  }

  getSiteName(): string {
    const settings = this.getSettings();

    return settings?.siteName;
  }

  getConditionalBalance(): boolean {
    const settings = this.getSettings();

    return 'conditionalBalance' in settings ? settings.conditionalBalance : true;
  }

  setTitle(title: string): void {
    this.title.setTitle(title);
  }

  setDescription(description: string) {
    this.meta.updateTag({name: 'description', content: description});
  }

  setKeywords(keywords: string) {
    this.meta.updateTag({name: 'keywords', content: keywords});
  }

  getSidebarState(): NbSidebarState {
    return localStorage.getItem('sidebar') as NbSidebarState;
  }

  setSidebarState(state: string): void {
    localStorage.setItem('sidebar', state);
  }

  getMenu(): Observable<NbMenuItem[]> {
    return this.menu$.pipe(shareReplay());
  }

  setCustomerGroup(group: CustomerGroup): void {
    localStorage.setItem('customerGroup', JSON.stringify(group));
    this.nextCustomerGroup(group);
  }

  getCustomerGroupLocal(): CustomerGroup {
    return this.getLocalObject<CustomerGroup>('customerGroup');
  }

  private getSessionObject<T>(name: string): T {
    return JSON.parse(sessionStorage.getItem(name));
  }

  private getLocalObject<T>(name: string): T {
    if (localStorage.getItem(name) !== null &&
      localStorage.getItem(name) !== undefined &&
      localStorage.getItem(name) !== 'undefined' &&
      localStorage.getItem(name) !== ''
    ) {
      return JSON.parse(localStorage.getItem(name));
    } else {
      return null;
    }
  }

  getTreeOpen(): boolean {
    return !localStorage.getItem('hideTree');
  }

  setTreeOpen(open: boolean): void {
    open ? localStorage.removeItem('hideTree') :  localStorage.setItem('hideTree', '1');
  }

  toggleTree() {
    this.setTreeOpen(!this.getTreeOpen());
  }

}
